import type {
  AssetProcessingActivitiesMetrics,
  ProcessingActivityCategoryPayloadDTO,
  ProcessingActivityDetail,
  ProcessingActivityPurposePayloadDTO,
} from 'config/api/processingActivities/processingActivities.types';
import sortRopaCategories from 'pages/ProcessingActivities/_shared/sortRopaCategories';
import {
  formatEditorPurposeRetentionPeriod,
  isLegitimateInterestsBasis,
} from 'pages/ProcessingActivities/ProcessingActivityEditorPage/processingActivityEditor.parsers';

import type {
  ProcessingActivityChangelogEntry,
  ProcessingActivityDetailPurpose,
  ProcessingActivityDetailViewModel,
} from './processingActivityDetail.types';

type ChangelogEntryDTO = {
  version_number?: number;
  published_at?: string;
  published_by_display_name?: string;
  published_by?: string;
  change_comment?: string;
  comment?: string;
};

const parseJsonValue = <T>(value: unknown): T | null => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'object') {
    return value as unknown as T;
  }

  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const parseJsonArray = <T>(value: unknown): T[] => {
  if (value == null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value as T[];
  }

  if (typeof value !== 'string' || !value.trim()) {
    return [];
  }

  const parsed = parseJsonValue<unknown>(value);
  return Array.isArray(parsed) ? (parsed as T[]) : [];
};

const isRetentionUnit = (value: unknown): value is 'days' | 'months' | 'years' =>
  value === 'days' || value === 'months' || value === 'years';

const mapCategoryDto = (dto: ProcessingActivityCategoryPayloadDTO) => ({
  id: dto.id,
  name: dto.name,
});

const mapPurposeDto = (dto: ProcessingActivityPurposePayloadDTO): ProcessingActivityDetailPurpose => {
  const retentionDescription =
    formatEditorPurposeRetentionPeriod({
      retentionTrigger: dto.retention_trigger ?? '',
      retentionValue: typeof dto.retention_value === 'number' ? dto.retention_value : null,
      retentionUnit: isRetentionUnit(dto.retention_unit) ? dto.retention_unit : '',
      retentionComment: dto.retention_comment ?? '',
    }) ||
    dto.retention_description ||
    dto.retention_comment ||
    '';

  return {
    id: dto.id,
    purposeText: dto.purpose_text ?? '',
    legalBasis: dto.legal_basis ?? '',
    legalBasisComment: dto.legal_basis_comment ?? '',
    retentionDescription,
    liaRequired: dto.lia_required ?? isLegitimateInterestsBasis(dto.legal_basis ?? ''),
    isDeleted: Boolean(dto.is_deleted),
  };
};

const mapChangelogEntry = (dto: ChangelogEntryDTO): ProcessingActivityChangelogEntry | null => {
  const versionNumber = dto.version_number;
  if (versionNumber == null) return null;

  return {
    versionNumber,
    publishedAt: dto.published_at ?? '',
    publishedByDisplayName: dto.published_by_display_name ?? dto.published_by ?? '',
    changeComment: dto.change_comment ?? dto.comment ?? '',
  };
};

export const parseProcessingActivityDetailViewModel = (detail: ProcessingActivityDetail): ProcessingActivityDetailViewModel => {
  const purposeDtos = parseJsonArray<ProcessingActivityPurposePayloadDTO>(detail.purposes);

  return {
    categories: {
      dataSubjectCategories: sortRopaCategories(
        parseJsonArray<ProcessingActivityCategoryPayloadDTO>(detail.dataSubjectCategories).map(mapCategoryDto),
      ),
      personalDataCategories: sortRopaCategories(
        parseJsonArray<ProcessingActivityCategoryPayloadDTO>(detail.personalDataCategories).map(mapCategoryDto),
      ),
      recipientCategories: sortRopaCategories(
        parseJsonArray<ProcessingActivityCategoryPayloadDTO>(detail.recipientCategories).map(mapCategoryDto),
      ),
    },
    changelog: parseJsonArray<ChangelogEntryDTO>(detail.changelog)
      .map(mapChangelogEntry)
      .filter((entry): entry is ProcessingActivityChangelogEntry => entry != null)
      .sort((left, right) => right.versionNumber - left.versionNumber),
    purposes: purposeDtos
      .map((dto, index) => ({ dto, index }))
      .sort((left, right) => (left.dto.sort_order ?? left.index) - (right.dto.sort_order ?? right.index))
      .map(({ dto }) => mapPurposeDto(dto)),
  };
};

type AssetProcessingActivitiesMetricsDTOShape = {
  linked_asset_processings_count?: number;
  awaiting_response_count?: number;
  submitted_count?: number;
  accepted_count?: number;
  dpia_indicated_count?: number;
  sensitive_personal_data_count?: number;
};

export const parseAssetProcessingActivitiesMetricsFromDetail = (
  detail: ProcessingActivityDetail,
): AssetProcessingActivitiesMetrics | null => {
  const parsed = parseJsonValue<AssetProcessingActivitiesMetricsDTOShape>(detail.assetProcessingActivitiesMetrics);
  if (!parsed) return null;

  return {
    linkedAssetProcessingsCount: parsed.linked_asset_processings_count ?? 0,
    awaitingResponseCount: parsed.awaiting_response_count ?? 0,
    submittedCount: parsed.submitted_count ?? 0,
    acceptedCount: parsed.accepted_count ?? 0,
    dpiaIndicatedCount: parsed.dpia_indicated_count ?? 0,
    sensitivePersonalDataCount: parsed.sensitive_personal_data_count ?? 0,
  };
};
